import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StandingsTable } from "@/components/StandingsTable";
import { FinalTablePredictor } from "@/components/FinalTablePredictor";
import { FixturePredictor } from "@/components/FixturePredictor";
import { BracketView } from "@/components/BracketView";
import { KnockoutBracket } from "@/components/KnockoutBracket";
import { SquadsView } from "@/components/SquadsView";
import { StatsView } from "@/components/StatsView";
import { NewsView } from "@/components/NewsView";
import { ShareCard } from "@/components/ShareCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, LogOut, Target, ListOrdered, TrendingUp, GitFork, Swords, Archive, Users, BarChart3, TriangleAlert, Crown, Newspaper, Flag } from "lucide-react";
import { useI18n, I18N_ENABLED } from "@/context/I18nContext";
import { LanguageSelect } from "@/components/LanguageSelect";
import { PrivateLeagues } from "@/components/PrivateLeagues";
import { TranslationEditor } from "@/components/TranslationEditor";
import { SyncHealthBadge } from "@/components/SyncHealthBadge";
import { AccessCodes } from "@/components/AccessCodes";
import { AdminUsers } from "@/components/AdminUsers";
import { AdminCleanupTestUsers } from "@/components/AdminCleanupTestUsers";
import { EmailHealth } from "@/components/EmailHealth";
import { ChangePassword } from "@/components/ChangePassword";
import { InviteButton } from "@/components/InviteButton";
import { CLAIM_KEY } from "@/pages/Claim";
import { OnboardingBanner } from "@/components/OnboardingBanner";
import { FavoriteClubControl } from "@/components/FavoriteClubControl";
import { PremiumButton, PremiumLock } from "@/components/Premium";
import { MatchReminders } from "@/components/MatchReminders";
import { DisplayNameEditor } from "@/components/DisplayNameEditor";

export default function Dashboard() {
  const { user, logout, updateUser, refreshUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [actual, setActual] = useState([]);
  const [predicted, setPredicted] = useState([]);
  const [accuracy, setAccuracy] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [archiveTable, setArchiveTable] = useState([]);
  const [tab, setTab] = useState("actual");
  const [nudge, setNudge] = useState(null);
  const [nudgeHidden, setNudgeHidden] = useState(false);
  const [scoreMd, setScoreMd] = useState(null);
  const [highlightTeam, setHighlightTeam] = useState(null);
  const [predictedOnce, setPredictedOnce] = useState(false);
  const [favName, setFavName] = useState("");
  const [plOpen, setPlOpen] = useState(false);
  const [inviteHidden, setInviteHidden] = useState(() => localStorage.getItem("gaffer-invite-cta-hidden") === "1");
  const hideInvite = () => { setInviteHidden(true); localStorage.setItem("gaffer-invite-cta-hidden", "1"); };
  const [newsDot, setNewsDot] = useState(false);
  const [clubLatest, setClubLatest] = useState(null);

  const seenKey = user?.favorite_team_id ? `gaffer-news-seen-${user.favorite_team_id}` : null;

  // Auto-redeem a claim code captured from a /claim/CODE link (e.g. after signup).
  useEffect(() => {
    if (!user?.id) return;
    const code = localStorage.getItem(CLAIM_KEY);
    if (!code) return;
    localStorage.removeItem(CLAIM_KEY);
    api.post("/redeem", { code }).then(() => {
      toast.success("Invite claimed — Premium unlocked for life! 🎉");
      refreshUser?.();
    }).catch((err) => {
      const d = err?.response?.data?.detail;
      if (d && !/already/i.test(d)) toast.info(d);
    });
  }, [user?.id, refreshUser]);

  useEffect(() => {
    if (!leagueId || !user?.favorite_team_id) { setNewsDot(false); return; }
    api.get(`/news/updates?league_id=${leagueId}`).then(({ data }) => {
      const latest = data.club_latest;
      setClubLatest(latest);
      if (!latest) { setNewsDot(false); return; }
      const seen = seenKey ? localStorage.getItem(seenKey) : null;
      setNewsDot(!seen || latest > seen);
    }).catch(() => {});
  }, [leagueId, user?.favorite_team_id, seenKey]);

  const markNewsSeen = () => {
    if (seenKey && clubLatest) localStorage.setItem(seenKey, clubLatest);
    setNewsDot(false);
  };

  const showOnboarding = !!user && !user.has_predictions && !predictedOnce;
  const hasFavorite = !!user?.favorite_team_id;

  useEffect(() => {
    if (showOnboarding && hasFavorite && !favName && user?.favorite_team_id) {
      api.get("/teams").then(({ data }) => {
        const tm = data.teams.find((x) => x.id === user.favorite_team_id);
        if (tm) setFavName(tm.name);
      }).catch(() => {});
    }
  }, [showOnboarding, hasFavorite, favName, user]);

  const onClubChosen = (teamId, lid, name) => {
    updateUser({ favorite_team_id: teamId, favorite_league_id: lid });
    setFavName(name);
    setHighlightTeam(teamId);
    setScoreMd(1);
    setTab("scores");
    setLeagueId(lid);
  };

  const onClubChanged = (teamId, lid, name) => {
    setFavName(name);
    setHighlightTeam(teamId);
    // reset the news 'seen' baseline so the club section reflects the new team
    if (leagueId) {
      api.get(`/news/updates?league_id=${leagueId}`).then(({ data }) => {
        setClubLatest(data.club_latest);
        const seen = localStorage.getItem(`gaffer-news-seen-${teamId}`);
        setNewsDot(!!data.club_latest && (!seen || data.club_latest > seen));
      }).catch(() => {});
    }
  };

  const onPredictNow = () => {
    if (user?.favorite_team_id) setHighlightTeam(user.favorite_team_id);
    setScoreMd(1);
    setTab("scores");
    if (user?.favorite_league_id) setLeagueId(user.favorite_league_id);
  };

  const onPredicted = () => { setPredictedOnce(true); setHighlightTeam(null); refresh(); };

  const [week1Share, setWeek1Share] = useState(false);
  const onWeekComplete = (md) => {
    if (md === 1 && leagueId && localStorage.getItem(`w1-share-${leagueId}`) !== "1") {
      localStorage.setItem(`w1-share-${leagueId}`, "1");
      setTimeout(() => setWeek1Share(true), 600);
    }
  };

  useEffect(() => {
    api.get("/leagues").then(({ data }) => {
      setLeagues(data);
      if (data.length) setLeagueId(data[0].id);
    });
  }, []);

  const loadLeague = async (id) => {
    setBracket(null);
    const league = leagues.find((l) => l.id === id);
    const isSwiss = league?.format === "SWISS";
    const calls = [
      api.get(`/leagues/${id}/fixtures`),
      api.get(`/leagues/${id}/standings`),
      api.get(`/leagues/${id}/predicted-standings`),
      api.get(`/leagues/${id}/accuracy`),
      api.get(`/leagues/${id}/archive`),
      api.get(`/leagues/${id}/nudge`),
    ];
    if (isSwiss) calls.push(api.get(`/leagues/${id}/predicted-bracket`));
    const [fx, st, ps, acc, arch, ndg, br] = await Promise.all(calls);
    const teamMap = {};
    fx.data.fixtures.forEach((f) => { if (f.home) teamMap[f.home.id] = f.home; if (f.away) teamMap[f.away.id] = f.away; });
    setTeams(Object.values(teamMap));
    setActual(st.data.table);
    setPredicted(ps.data.table);
    setAccuracy(acc.data);
    setArchiveTable(arch.data.table);
    setNudge(ndg.data);
    setNudgeHidden(ndg.data.matchday ? localStorage.getItem(`nudge-dismiss-${id}-${ndg.data.matchday}`) === "1" : false);
    setBracket(isSwiss ? br.data : null);
    if (!isSwiss && (tab === "bracket" || tab === "knockout")) setTab("actual");
  };

  useEffect(() => { if (leagueId && leagues.length) loadLeague(leagueId); }, [leagueId, leagues]);

  const refresh = () => leagueId && loadLeague(leagueId);
  const currentLeague = leagues.find((l) => l.id === leagueId);
  const isSwiss = currentLeague?.format === "SWISS";

  const [lbRows, setLbRows] = useState([]);
  const openLeaderboard = async () => {
    const { data } = await api.get("/leaderboard");
    setLbRows(data.leaderboard);
  };
  const goToNudge = () => { if (nudge?.matchday) setScoreMd(nudge.matchday); setTab("scores"); };
  const dismissNudge = () => {
    if (nudge?.matchday) localStorage.setItem(`nudge-dismiss-${leagueId}-${nudge.matchday}`, "1");
    setNudgeHidden(true);
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 bg-background border-b-2 border-foreground">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl tracking-tight">GAFFER<span className="text-primary">.</span></span>
            <span className="hidden md:inline font-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Prediction Desk</span>
          </div>
          <div className="flex items-center gap-1 md:gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/leaderboard")} data-testid="leaderboard-btn">
              <BarChart3 size={16} className="me-2" /> <span className="hidden sm:inline">{t("lb.button")}</span>
            </Button>
            <PrivateLeagues leagues={leagues} currentLeagueId={leagueId} open={plOpen} onOpenChange={setPlOpen} />
            {user?.role === "admin" && <SyncHealthBadge />}
            {user?.role === "admin" && <AccessCodes />}
            {user?.role === "admin" && <AdminUsers />}
            {user?.role === "admin" && <AdminCleanupTestUsers />}
            {user?.role === "admin" && <EmailHealth />}
            <MatchReminders />
            {I18N_ENABLED && user?.role === "admin" && <TranslationEditor />}
            <LanguageSelect />
            <FavoriteClubControl user={user} updateUser={updateUser} onChanged={onClubChanged} />
            <InviteButton />
            <PremiumButton />
            <span className="text-sm text-muted-foreground hidden sm:block font-data" data-testid="user-name">{user?.name}</span>
            <ChangePassword />
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="logout-btn">
              <LogOut size={16} className="me-2" /> <span className="hidden sm:inline">{t("nav.logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="overline text-primary mb-2">{t("dash.desk")}</p>
            <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none flex items-center gap-3">
              <span className="text-3xl">{currentLeague?.emoji}</span>{currentLeague?.name || "League"}
            </h1>
          </div>
          <Select value={leagueId || ""} onValueChange={setLeagueId}>
            <SelectTrigger className="w-56 bg-card border-2 border-border rounded-none h-11 font-data uppercase text-xs tracking-wider" data-testid="league-select">
              <SelectValue placeholder={t("dash.selectLeague")} />
            </SelectTrigger>
            <SelectContent className="bg-popover border-2 border-foreground rounded-none">
              {leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`league-option-${l.code}`} className="rounded-none">
                  {l.emoji} {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pre-season notice: scoring starts at the configured matchday */}
        {(user?.scoring_start_matchday ?? 5) > 1 && (
          <div data-testid="preseason-banner"
            className="flex items-start gap-3 border-2 border-[hsl(var(--stadium-yellow))] bg-[hsl(var(--stadium-yellow))]/10 px-4 py-3 mb-6">
            <Flag className="text-[hsl(var(--stadium-yellow))] shrink-0 mt-0.5" size={18} />
            <p className="text-sm">
              <span className="font-bold uppercase font-data text-xs tracking-wide me-1 text-[hsl(var(--stadium-yellow))]">Pre-season · predictions open</span>
              Scoring starts <span className="font-bold">Matchday {user?.scoring_start_matchday ?? 5}</span>. Weeks 1–{(user?.scoring_start_matchday ?? 5) - 1} are free warm-up — tweak your calls as much as you like until then, while the transfer window closes (1 Sep) and squads are finalised.
            </p>
          </div>
        )}


        {showOnboarding && (
          <OnboardingBanner
            favoriteName={hasFavorite ? (favName || "your club") : null}
            onChosen={onClubChosen}
            onPredictNow={onPredictNow}
          />
        )}

        {/* Matchday reminder nudge */}
        {!showOnboarding && nudge && !nudge.all_done && !nudgeHidden && (
          <div data-testid="nudge-banner" className="flex flex-wrap items-center gap-3 border-2 border-primary bg-primary/10 px-4 py-3 mb-6">
            <TriangleAlert className="text-primary shrink-0" size={18} />
            <p className="text-sm flex-1 min-w-[200px]">
              <span className="font-bold uppercase font-data text-xs tracking-wide me-1">{t("nudge.title")}</span>
              {t("nudge.body", { n: nudge.matchday, c: nudge.unpredicted, t: nudge.total, count: nudge.unpredicted })}
            </p>
            <Button size="sm" onClick={goToNudge} data-testid="nudge-cta">{t("nudge.cta", { n: nudge.matchday })}</Button>
            <Button size="sm" variant="ghost" onClick={dismissNudge} data-testid="nudge-dismiss">{t("nudge.dismiss")}</Button>
          </div>
        )}

        {/* Coupon stat bar */}
        {accuracy && (
          <div className="grid grid-cols-2 md:grid-cols-5 border-2 border-foreground mb-2" data-testid="stat-bar">
            <StatCell label={t("stat.points")} value={accuracy.points} highlight testid="stat-points" />
            <StatCell label={t("stat.exact")} value={accuracy.exact} testid="stat-exact" />
            <StatCell label="Close" value={accuracy.close ?? 0} testid="stat-close" />
            <StatCell label={t("stat.correct")} value={accuracy.correct_results} testid="stat-correct" />
            <StatCell label={t("stat.predicted")} value={`${accuracy.predicted}/${accuracy.finished_total}`} testid="stat-predicted" last />
          </div>
        )}
        {accuracy && (
          <p className="text-[11px] font-data uppercase tracking-wider text-muted-foreground mb-8 text-center md:text-left" data-testid="scoring-legend">
            Scoring · <span className="text-foreground">3</span> exact score · <span className="text-foreground">1.5</span> close (right result, within a goal) · <span className="text-foreground">1</span> correct result
          </p>
        )}

        {!showOnboarding && !inviteHidden && (
          <div data-testid="invite-cta" className="flex flex-wrap items-center gap-3 border-2 border-primary bg-gradient-to-r from-primary/15 to-transparent px-4 py-3 mb-6">
            <Trophy className="text-primary shrink-0" size={20} />
            <p className="text-sm flex-1 min-w-[200px]">
              <span className="font-display text-lg uppercase me-2">Start a mini-league</span>
              <span className="text-muted-foreground">Predict against your mates — create a Banter league and share the invite code.</span>
            </p>
            <Button size="sm" onClick={() => setPlOpen(true)} data-testid="invite-cta-btn"
              className="rounded-none uppercase text-xs font-bold tracking-wide">
              <Users size={14} className="me-1.5" /> Create your mini-league
            </Button>
            <Button size="sm" variant="ghost" onClick={hideInvite} data-testid="invite-cta-dismiss" className="text-muted-foreground">Dismiss</Button>
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => { setTab(v); refresh(); if (v === "news") markNewsSeen(); }}>
          <TabsList className="bg-transparent border-b-2 border-border rounded-none w-full justify-start h-auto p-0 gap-5 mb-6 overflow-x-auto flex-nowrap">
            <TabTrigger value="actual" icon={ListOrdered} label={t("tab.actual")} testid="tab-actual" />
            <TabTrigger value="predicted" icon={TrendingUp} label={t("tab.predicted")} testid="tab-predicted" />
            <TabTrigger value="rank" icon={Trophy} label={t("tab.rank")} testid="tab-rank" />
            <TabTrigger value="scores" icon={Target} label={t("tab.scores")} testid="tab-scores" />
            <TabTrigger value="archive" icon={Archive} label={t("tab.archive")} testid="tab-archive" />
            <TabTrigger value="squads" icon={Users} label={t("tab.squads")} testid="tab-squads" />
            <TabTrigger value="stats" icon={BarChart3} label="Stats" testid="tab-stats" />
            <TabTrigger value="news" icon={Newspaper} label="News" testid="tab-news" dot={newsDot} />
            {isSwiss && <TabTrigger value="bracket" icon={GitFork} label={t("tab.bracket")} testid="tab-bracket" />}
            {isSwiss && <TabTrigger value="knockout" icon={Swords} label={t("tab.knockout")} testid="tab-knockout" />}
          </TabsList>

          <TabsContent value="actual">
            <StandingsTable table={actual} title={t("dash.actualTitle")} />
          </TabsContent>
          <TabsContent value="predicted">
            <div className="flex justify-end mb-3">
              <ShareCard leagueId={leagueId} leagueName={currentLeague?.name} />
            </div>
            <StandingsTable table={predicted} title={t("dash.predictedTitle")} compareTo={actual} />
            <p className="text-muted-foreground text-xs mt-3 font-data">{t("dash.predictedHint")}</p>
          </TabsContent>
          <TabsContent value="rank">
            {teams.length > 0 && <FinalTablePredictor leagueId={leagueId} teams={teams} />}
          </TabsContent>
          <TabsContent value="scores">
            {leagueId && <FixturePredictor leagueId={leagueId} initialMatchday={scoreMd} highlightTeamId={highlightTeam} onPredicted={onPredicted} onWeekComplete={onWeekComplete} isPremium={!!user?.is_premium} />}
          </TabsContent>
          <TabsContent value="archive">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-muted-foreground">{t("archive.title")}</p>
              <span className="font-data text-[10px] uppercase tracking-wider text-muted-foreground border-2 border-border px-1.5 py-0.5">{t("archive.sample")}</span>
            </div>
            <StandingsTable table={archiveTable} />
          </TabsContent>
          <TabsContent value="squads">
            {leagueId && (user?.is_premium
              ? <SquadsView leagueId={leagueId} />
              : <PremiumLock title="Squads & Player Ratings" blurb="See real club rosters and rate every player. Unlock with GAFFER Premium." />)}
          </TabsContent>
          <TabsContent value="stats">
            {leagueId && (user?.is_premium
              ? <StatsView leagueId={leagueId} />
              : <PremiumLock title="Season Stats" blurb="Top scorers, assists, clean sheets and cards across the season. Unlock with GAFFER Premium." />)}
          </TabsContent>
          <TabsContent value="news">
            {leagueId && <NewsView leagueId={leagueId} />}
          </TabsContent>
          {isSwiss && (
            <TabsContent value="bracket">
              <BracketView bracket={bracket} />
            </TabsContent>
          )}
          {isSwiss && (
            <TabsContent value="knockout">
              <KnockoutBracket leagueId={leagueId} />
            </TabsContent>
          )}
        </Tabs>

        {leagueId && (
          <ShareCard
            leagueId={leagueId}
            leagueName={currentLeague?.name}
            open={week1Share}
            onOpenChange={setWeek1Share}
            hideTrigger
            heading="Week 1 locked in"
            subheading="Post your predicted table and pull your mates in for the banter."
          />
        )}
      </main>
    </div>
  );
}

function StatCell({ label, value, highlight, testid, last }) {
  return (
    <div className={`p-5 border-b-2 md:border-b-0 border-border ${last ? "" : "border-e-2"} ${highlight ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid={testid}>
      <p className={`overline ${highlight ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{label}</p>
      <p className="font-data font-bold text-4xl mt-2 leading-none">{value}</p>
    </div>
  );
}

function TabTrigger({ value, icon: Icon, label, testid, dot }) {
  return (
    <TabsTrigger value={value} data-testid={testid}
      className="rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-muted-foreground font-semibold uppercase tracking-wide text-xs whitespace-nowrap shrink-0">
      <Icon size={15} className="me-2" /> {label}
      {dot && <span className="ml-1.5 w-2 h-2 rounded-full bg-primary animate-pulse" data-testid={`${testid}-dot`} />}
    </TabsTrigger>
  );
}
